import React from "react";
import { PageHero, PageBody, formatDate } from "./_shared";
import { useNursing } from "./NursingLayout";
import { FileText, ExternalLink, GraduationCap, Phone, Mail, MessageCircle } from "lucide-react";

export default function Admissions() {
  const data = useNursing();
  const inst = data.institute || {};
  const notices = data.notices_admission || [];

  return (
    <div data-testid="nur-admissions">
      <PageHero eyebrow="Admissions 2025–26" title="Apply, visit, ask"
        subtitle="Take the next step. Our admissions team will walk you through eligibility, dates, and document checklists."
        accent="orange" />
      <PageBody>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <h2 className="font-['Poppins',sans-serif] font-bold text-2xl text-[#0F2A5F] mb-4">Latest Admission Notices</h2>
            <div className="space-y-3">
              {notices.length === 0 && (
                <div className="bg-white rounded-xl p-8 text-center text-sm text-[#475569] ring-1 ring-[#E2E8F0]">
                  No admission notices have been posted yet. Reach out using the details on the right.
                </div>
              )}
              {notices.map((n) => (
                <a key={n.id} href={n.file_url || n.external_url || "#"} target="_blank" rel="noopener noreferrer"
                   className="block bg-white rounded-xl p-4 ring-1 ring-[#E2E8F0] hover:ring-2 hover:ring-[#0FB28A] transition-all">
                  <div className="flex items-start gap-3">
                    <FileText className="text-[#0FB28A] mt-0.5" size={18} />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-[#0FB28A] font-bold">{formatDate(n.posted_on || n.notice_date)}</div>
                      <div className="text-sm mt-1 text-[#0F2A5F] font-semibold">{n.title}</div>
                    </div>
                    <ExternalLink className="text-[#475569]" size={14} />
                  </div>
                </a>
              ))}
            </div>
          </div>
          <aside className="bg-gradient-to-br from-[#0F2A5F] to-[#1B3E83] text-white rounded-2xl p-7 shadow-lg relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#0FB28A]/30 rounded-full blur-2xl" />
            <div className="relative">
              <GraduationCap size={24} strokeWidth={1.5} className="text-[#7FE7CE]" />
              <h3 className="mt-2 font-['Poppins',sans-serif] font-bold text-xl">Talk to admissions</h3>
              <ul className="mt-4 space-y-3 text-sm">
                {inst.admission_phone && (
                  <li className="flex items-center gap-2">
                    <Phone size={13} className="text-[#7FE7CE]" />
                    <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="hover:text-[#7FE7CE]">{inst.admission_phone}</a>
                  </li>
                )}
                {inst.email && (
                  <li className="flex items-center gap-2">
                    <Mail size={13} className="text-[#7FE7CE]" />
                    <a href={`mailto:${inst.email}`} className="hover:text-[#7FE7CE] break-all">{inst.email}</a>
                  </li>
                )}
                {inst.whatsapp_phone && (
                  <li className="flex items-center gap-2">
                    <MessageCircle size={13} className="text-[#7FE7CE]" />
                    <a href={`https://wa.me/${inst.whatsapp_phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#7FE7CE]">WhatsApp us</a>
                  </li>
                )}
              </ul>
              <p className="mt-5 text-[11px] text-white/70 leading-relaxed">
                Eligibility, document checklist, and fee structure shared on request. We can also schedule a campus visit or virtual tour.
              </p>
            </div>
          </aside>
        </div>
      </PageBody>
    </div>
  );
}
